import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const SpinEventPage = () => {
  const { month, year } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [timeToStart, setTimeToStart] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [availableMonths, setAvailableMonths] = useState([]);
  const [isEventStarted, setIsEventStarted] = useState(true);
  const [hasEventEnded, setHasEventEnded] = useState(false);

  // Prize structure
  // Süresi dolmuş (geçmiş) etkinliklerde eski tablo, aktif/gelecek etkinliklerde yeni tablo gösterilir
  const OLD_PRIZES = {
    1: '800$',
    2: '500$',
    3: '400$',
    4: '350$',
    5: '300$',
    6: '250$',
    7: '200$',
    8: '100$',
    9: '75$',
    10: '25$'
  };
  const NEW_PRIZES = {
    1: '1,200$',
    2: '800$',
    3: '600$',
    4: '500$',
    5: '400$',
    6: '350$',
    7: '300$',
    8: '250$',
    9: '150$',
    10: '100$',
    11: '100$',
    12: '75$',
    13: '75$',
    14: '50$',
    15: '50$'
  };
  const prizes = hasEventEnded ? OLD_PRIZES : NEW_PRIZES;

  useEffect(() => {
    fetchAvailableMonths();
    fetchEvent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, year]);

  // Türkiye saatini al (UTC+3)
  const getTurkeyTime = () => {
    const now = new Date();
    // Türkiye saati UTC+3, bu yüzden UTC'ye 3 saat ekliyoruz
    // JavaScript Date zaten local timezone kullanıyor, bu yüzden direkt kullanabiliriz
    // Ama UTC tarihlerini Türkiye saatine çevirmek için +3 saat ekliyoruz
    return now;
  };

  useEffect(() => {
    if (event) {
      const startDateUtc = new Date(event.startDateUtc || event.start_date_utc);
      const startDateTurkey = new Date(startDateUtc.getTime() - (3 * 60 * 60 * 1000)); // UTC-3
      const endDateUtc = new Date(event.endDateUtc || event.end_date_utc);
      const endDateTurkey = new Date(endDateUtc.getTime() - (3 * 60 * 60 * 1000)); // UTC-3
      const now = getTurkeyTime();
      setIsEventStarted(now >= startDateTurkey);
      setHasEventEnded(now >= endDateTurkey);

      if (isEventStarted) {
        fetchLeaderboard();
      }
      
      const interval = setInterval(() => {
        updateTimeRemaining();
        updateTimeToStart();
      }, 1000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, isEventStarted]);

  const fetchAvailableMonths = async () => {
    try {
      // Use public endpoint instead of admin endpoint
      const response = await axios.get('/api/spin-events').catch(() => ({ data: [] }));
      
      const events = response.data || [];
      
      // Extract unique months/years
      const monthsMap = new Map();
      events.forEach(event => {
        const startDate = new Date(event.startDateUtc || event.start_date_utc);
        const monthKey = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}`;
        if (!monthsMap.has(monthKey)) {
          monthsMap.set(monthKey, {
            year: startDate.getFullYear(),
            month: startDate.getMonth() + 1,
            monthName: startDate.toLocaleString('tr-TR', { month: 'long', year: 'numeric' })
          });
        }
      });
      
      setAvailableMonths(Array.from(monthsMap.values()).sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        return b.month - a.month;
      }));
    } catch (error) {
      console.error('Error fetching available months:', error);
    }
  };

  const fetchEvent = async () => {
    try {
      setLoading(true);
      
      let response;
      if (month && year) {
        // Fetch specific month/year event using public endpoint
        const allEventsResponse = await axios.get('/api/spin-events').catch(() => ({ data: [] }));
        
        const events = allEventsResponse.data || [];
        const targetDate = new Date(parseInt(year), parseInt(month) - 1, 1);
        
        const matchingEvent = events.find(event => {
          const startDate = new Date(event.startDateUtc || event.start_date_utc);
          return startDate.getFullYear() === targetDate.getFullYear() &&
                 startDate.getMonth() === targetDate.getMonth();
        });
        
        if (matchingEvent) {
          response = { data: matchingEvent };
        } else {
          // En son etkinliği göster
          if (events.length > 0) {
            const sortedEvents = events.sort((a, b) => {
              const dateA = new Date(a.startDateUtc || a.start_date_utc);
              const dateB = new Date(b.startDateUtc || b.start_date_utc);
              return dateB - dateA;
            });
            response = { data: sortedEvents[0] };
          } else {
            response = { data: null };
          }
        }
      } else {
        // Fetch active event or latest event
        try {
          response = await axios.get('/api/spin-events/active');
        } catch (error) {
          // Aktif etkinlik yoksa en son etkinliği al
          const allEvents = await axios.get('/api/spin-events').catch(() => ({ data: [] }));
          if (allEvents.data && allEvents.data.length > 0) {
            const sortedEvents = allEvents.data.sort((a, b) => {
              const dateA = new Date(a.startDateUtc || a.start_date_utc);
              const dateB = new Date(b.startDateUtc || b.start_date_utc);
              return dateB - dateA;
            });
            response = { data: sortedEvents[0] };
          }
        }
      }
      
      if (response && response.data) {
        setEvent(response.data);
        // Etkinlik başladı mı kontrol et (Türkiye saatine göre, UTC-3)
        const startDateUtc = new Date(response.data.startDateUtc || response.data.start_date_utc);
        const startDateTurkey = new Date(startDateUtc.getTime() - (3 * 60 * 60 * 1000)); // UTC-3
        const now = getTurkeyTime();
        setIsEventStarted(now >= startDateTurkey);
      }
    } catch (error) {
      console.error('Error fetching event:', error);
      // Hata durumunda da en son etkinliği çekmeyi dene
      try {
        const allEvents = await axios.get('/api/spin-events').catch(() => ({ data: [] }));
        if (allEvents.data && allEvents.data.length > 0) {
          const sortedEvents = allEvents.data.sort((a, b) => {
            const dateA = new Date(a.startDateUtc || a.start_date_utc);
            const dateB = new Date(b.startDateUtc || b.start_date_utc);
            return dateB - dateA;
          });
          setEvent(sortedEvents[0]);
          const startDateUtc = new Date(sortedEvents[0].startDateUtc || sortedEvents[0].start_date_utc);
          const startDateTurkey = new Date(startDateUtc.getTime() - (3 * 60 * 60 * 1000)); // UTC-3
          const now = getTurkeyTime();
          setIsEventStarted(now >= startDateTurkey);
        }
      } catch (err) {
        console.error('Error fetching latest event:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    if (!event || !event.id) return;
    
    try {
      const response = await axios.get(`/api/spin-events/${event.id}/leaderboard`);
      if (response.data && response.data.data) {
        setLeaderboard(response.data.data.slice(0, 25));
      } else {
        setLeaderboard([]);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      setLeaderboard([]);
    }
  };

  const updateTimeRemaining = () => {
    if (!event) return;
    
    // UTC tarihini Türkiye saatine çevir (UTC-3, 6 saat geri)
    const endDateUtc = new Date(event.endDateUtc || event.end_date_utc);
    // UTC tarihinden 3 saat çıkararak Türkiye saatine çeviriyoruz
    const endDateTurkey = new Date(endDateUtc.getTime() - (3 * 60 * 60 * 1000)); // UTC-3
    
    const now = getTurkeyTime();
    const diff = endDateTurkey - now;
    
    if (diff <= 0) {
      setTimeRemaining({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      setHasEventEnded(true);
      return;
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    setTimeRemaining({ days, hours, minutes, seconds });
    setHasEventEnded(false);
  };

  const updateTimeToStart = () => {
    if (!event) return;
    
    // UTC tarihini Türkiye saatine çevir (UTC-3, 6 saat geri)
    const startDateUtc = new Date(event.startDateUtc || event.start_date_utc);
    // UTC tarihinden 3 saat çıkararak Türkiye saatine çeviriyoruz
    const startDateTurkey = new Date(startDateUtc.getTime() - (3 * 60 * 60 * 1000)); // UTC-3
    
    const now = getTurkeyTime();
    const diff = startDateTurkey - now;
    
    if (diff <= 0) {
      setTimeToStart({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      setIsEventStarted(true);
      return;
    }
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);
    
    setTimeToStart({ days, hours, minutes, seconds });
    setIsEventStarted(false);
  };

  const maskUsername = (username) => {
    if (!username || username.length <= 2) return username || '';
    if (username.length <= 4) {
      return username[0] + '*'.repeat(username.length - 2) + username[username.length - 1];
    }
    const start = Math.floor(username.length / 3);
    const end = username.length - start;
    return username.substring(0, start) + '*'.repeat(end - start) + username.substring(end);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount) + '$';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-navy-950 via-navy-900 to-navy-950 flex items-center justify-center">
        <div className="text-white text-xl">Yükleniyor...</div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-navy-950 via-navy-900 to-navy-950 flex items-center justify-center px-4">
        <div className="text-white text-xl text-center">
          Henüz etkinlik bulunmamaktadır.
        </div>
      </div>
    );
  }

  // Helper function to get full image URL
  const getImageUrl = (url) => {
    if (!url) return null;
    // If already a full URL (http/https), return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // If starts with /uploads, return as is (will be handled by server)
    if (url.startsWith('/uploads/')) {
      return url;
    }
    // If it's just a filename (like spin-event-xxx.png), prepend /uploads/
    if (url.includes('spin-event-') || url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) {
      return url.startsWith('/') ? url : `/uploads/${url}`;
    }
    // Otherwise, prepend /uploads/
    return url.startsWith('/') ? url : `/uploads/${url}`;
  };

  const backgroundImageUrl = getImageUrl(event.backgroundImageUrl || event.background_image_url);
  const logoUrl = getImageUrl(event.logoUrl || event.logo_url);

  const backgroundStyle = backgroundImageUrl
    ? {
        backgroundImage: `url(${backgroundImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }
    : {};

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={backgroundStyle}
    >
      {/* Background overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-navy-950/90 via-navy-900/80 to-navy-950/90">
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Logo - En Üstte */}
        {logoUrl && (
          <div className="flex justify-center mb-8 pt-4">
            <img
              src={logoUrl}
              alt="Logo"
              className="max-h-32 md:max-h-40 object-contain"
              onError={(e) => {
                console.warn('Logo image failed to load:', logoUrl);
                // Hide the image element
                e.target.style.display = 'none';
                // Also hide the parent div if image fails
                if (e.target.parentElement) {
                  e.target.parentElement.style.display = 'none';
                }
              }}
              onLoad={() => {
                // Image loaded successfully, ensure it's visible
                const img = document.querySelector(`img[src="${logoUrl}"]`);
                if (img) {
                  img.style.display = '';
                  if (img.parentElement) {
                    img.parentElement.style.display = '';
                  }
                }
              }}
            />
          </div>
        )}

        {/* Month selector */}
        {availableMonths.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2 justify-center">
            {availableMonths.map((monthData) => (
              <button
                key={`${monthData.year}-${monthData.month}`}
                onClick={() => navigate(`/spin-event/${monthData.year}/${monthData.month}`)}
                className={`px-4 py-2 rounded-lg font-bold transition-all ${
                  (month && year && parseInt(month) === monthData.month && parseInt(year) === monthData.year) ||
                  (!month && !year && availableMonths.length > 0 && monthData.year === availableMonths[0].year && monthData.month === availableMonths[0].month)
                    ? 'bg-gold text-navy-900 shadow-lg'
                    : 'bg-navy-800/50 text-gold border border-gold/30 hover:bg-navy-700/50'
                }`}
              >
                {monthData.monthName}
              </button>
            ))}
          </div>
        )}

        {/* Event Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">
            {event.prizeAmount || event.prize_amount || '3,000.00$'}
          </h1>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-6 drop-shadow-lg">
            {event.title || 'Büyük Ödüllü Çevrim Etkinliği'}
          </h2>

          {/* Countdown Timer */}
          <div className="mb-6">
            <div className="text-white text-lg mb-2">
              {isEventStarted ? 'Bitmesine Kalan Süre:' : 'Başlamasına Kalan Süre:'}
            </div>
            <div className="flex justify-center gap-4 md:gap-6 flex-wrap">
              <div className="bg-navy-800/70 px-4 py-2 rounded-lg border border-gold/30">
                <div className="text-2xl font-bold text-gold">
                  {isEventStarted ? timeRemaining.days : timeToStart.days}
                </div>
                <div className="text-sm text-white">GÜN</div>
              </div>
              <div className="bg-navy-800/70 px-4 py-2 rounded-lg border border-gold/30">
                <div className="text-2xl font-bold text-gold">
                  {isEventStarted ? timeRemaining.hours : timeToStart.hours}
                </div>
                <div className="text-sm text-white">SAAT</div>
              </div>
              <div className="bg-navy-800/70 px-4 py-2 rounded-lg border border-gold/30">
                <div className="text-2xl font-bold text-gold">
                  {isEventStarted ? timeRemaining.minutes : timeToStart.minutes}
                </div>
                <div className="text-sm text-white">DAKİKA</div>
              </div>
              <div className="bg-navy-800/70 px-4 py-2 rounded-lg border border-gold/30">
                <div className="text-2xl font-bold text-gold">
                  {isEventStarted ? timeRemaining.seconds : timeToStart.seconds}
                </div>
                <div className="text-sm text-white">SANİYE</div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center gap-4 flex-wrap">
            {(event.buttonLink || event.button_link) && (
              <a
                href={event.buttonLink || event.button_link}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-navy-800 to-navy-900 hover:from-navy-700 hover:to-navy-800 text-white font-bold px-8 py-3 rounded-lg transition-all shadow-lg hover:shadow-xl"
              >
                {event.buttonText || event.button_text || 'Hemen Oyna'}
              </a>
            )}
            {(event.rulesButtonLink || event.rules_button_link) && (
              <a
                href={event.rulesButtonLink || event.rules_button_link}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-navy-800/70 hover:bg-navy-700/70 text-white font-bold px-8 py-3 rounded-lg transition-all border border-gold/30"
              >
                {event.rulesButtonText || event.rules_button_text || 'Kurallar Ve Detaylar'}
              </a>
            )}
          </div>
        </div>

        {/* Top 3 Podium */}
        {isEventStarted && leaderboard.length > 0 && (
          <div className="mb-12">
            <div className="flex flex-row items-end justify-center gap-2 md:gap-4 mb-8 px-2">
              {/* 2nd Place */}
              {leaderboard[1] && (
                <div className="flex-1 max-w-[140px] md:max-w-[192px] bg-gradient-to-b from-slate-300/80 to-slate-500/80 rounded-lg p-2 md:p-3 border-2 border-slate-200 shadow-xl backdrop-blur-sm">
                  <div className="text-center mb-1">
                    <div className="text-2xl md:text-3xl font-bold text-white mb-0.5">2</div>
                    {/* Avatar with ikinci.png frame */}
                    <div className="relative mx-auto mb-1.5 md:mb-2 flex items-center justify-center" style={{ width: '100px', height: '100px' }}>
                      {/* ikinci.png frame */}
                      <img 
                        src="/icons/ikinci.png" 
                        alt="2nd Place Frame" 
                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full object-contain pointer-events-none"
                        style={{ zIndex: 1, maxWidth: '100px', maxHeight: '100px' }}
                        onError={(e) => {
                          console.warn('ikinci.png not found at /icons/ikinci.png');
                          e.target.style.display = 'none';
                        }}
                      />
                      {/* Profile icon - centered inside frame */}
                      <span 
                        className="relative text-lg md:text-xl"
                        style={{ 
                          zIndex: 2,
                          filter: 'brightness(0) invert(1)',
                          display: 'inline-block'
                        }}
                      >
                        👤
                      </span>
                    </div>
                    <div className="text-sm md:text-lg font-bold text-white mb-0.5 truncate px-1 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                      {leaderboard[1].masked_username || maskUsername(leaderboard[1].user_name)}
                    </div>
                    <div className="text-[10px] md:text-xs text-white/80 mb-0.5">Yapılan Çevrim</div>
                    <div className="text-xs md:text-base font-bold text-white mb-1.5 md:mb-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                      {formatCurrency(leaderboard[1].wagered)}
                    </div>
                    <div className="bg-blue-900/95 px-1.5 md:px-3 py-0.5 md:py-1 rounded-lg border-2 border-blue-600 shadow-lg">
                      <div className="text-yellow-300 font-extrabold text-xs md:text-lg drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                        Ödül: {prizes[2]}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 1st Place */}
              {leaderboard[0] && (
                <div className="flex-1 max-w-[160px] md:max-w-[224px] bg-gradient-to-b from-yellow-500/90 to-yellow-700/90 rounded-lg p-2 md:p-3 border-2 border-yellow-300 shadow-2xl backdrop-blur-sm transform -translate-y-2 md:-translate-y-4">
                  <div className="text-center mb-1">
                    <div className="text-3xl md:text-4xl font-bold text-white mb-0.5 md:mb-1">1</div>
                    {/* Avatar with birinci.png frame */}
                    <div className="relative mx-auto mb-1.5 md:mb-2 flex items-center justify-center" style={{ width: '120px', height: '120px' }}>
                      {/* birinci.png frame */}
                      <img 
                        src="/icons/birinci.png" 
                        alt="1st Place Frame" 
                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full object-contain pointer-events-none"
                        style={{ zIndex: 1, maxWidth: '120px', maxHeight: '120px' }}
                        onError={(e) => {
                          console.warn('birinci.png not found at /icons/birinci.png');
                          e.target.style.display = 'none';
                        }}
                      />
                      {/* Profile icon - centered inside frame */}
                      <span 
                        className="relative text-xl md:text-2xl"
                        style={{ 
                          zIndex: 2,
                          filter: 'brightness(0) invert(1)',
                          display: 'inline-block'
                        }}
                      >
                        👤
                      </span>
                    </div>
                    <div className="text-base md:text-xl font-bold text-white mb-0.5 truncate px-1">
                      {leaderboard[0].masked_username || maskUsername(leaderboard[0].user_name)}
                    </div>
                    <div className="text-[10px] md:text-xs text-white/80 mb-0.5">Yapılan Çevrim</div>
                    <div className="text-sm md:text-lg font-bold text-white mb-1.5 md:mb-2">
                      {formatCurrency(leaderboard[0].wagered)}
                    </div>
                    <div className="bg-blue-900/95 px-2 md:px-4 py-0.5 md:py-1.5 rounded-lg border-2 border-blue-600 shadow-lg">
                      <div className="text-yellow-300 font-extrabold text-sm md:text-2xl drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                        Ödül: {prizes[1]}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* 3rd Place */}
              {leaderboard[2] && (
                <div className="flex-1 max-w-[140px] md:max-w-[192px] bg-gradient-to-b from-amber-700/80 to-amber-900/80 rounded-lg p-2 md:p-3 border-2 border-amber-500 shadow-xl backdrop-blur-sm">
                  <div className="text-center mb-1">
                    <div className="text-2xl md:text-3xl font-bold text-white mb-0.5">3</div>
                    {/* Avatar with ucuncu.png frame */}
                    <div className="relative mx-auto mb-1.5 md:mb-2 flex items-center justify-center" style={{ width: '100px', height: '100px' }}>
                      {/* ucuncu.png frame */}
                      <img 
                        src="/icons/ucuncu.png" 
                        alt="3rd Place Frame" 
                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full object-contain pointer-events-none"
                        style={{ zIndex: 1, maxWidth: '100px', maxHeight: '100px' }}
                        onError={(e) => {
                          console.warn('ucuncu.png not found at /icons/ucuncu.png');
                          e.target.style.display = 'none';
                        }}
                      />
                      {/* Profile icon - centered inside frame */}
                      <span 
                        className="relative text-lg md:text-xl"
                        style={{ 
                          zIndex: 2,
                          filter: 'brightness(0) invert(1)',
                          display: 'inline-block'
                        }}
                      >
                        👤
                      </span>
                    </div>
                    <div className="text-sm md:text-lg font-bold text-white mb-0.5 truncate px-1 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">
                      {leaderboard[2].masked_username || maskUsername(leaderboard[2].user_name)}
                    </div>
                    <div className="text-[10px] md:text-xs text-white/80 mb-0.5">Yapılan Çevrim</div>
                    <div className="text-xs md:text-base font-bold text-white mb-1.5 md:mb-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                      {formatCurrency(leaderboard[2].wagered)}
                    </div>
                    <div className="bg-blue-900/95 px-1.5 md:px-3 py-0.5 md:py-1 rounded-lg border-2 border-blue-600 shadow-lg">
                      <div className="text-yellow-300 font-extrabold text-xs md:text-lg drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
                        Ödül: {prizes[3]}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Rest of Leaderboard */}
            {leaderboard.length > 3 && (
              <div className="space-y-2">
                {leaderboard.slice(3).map((entry, index) => {
                  const rank = index + 4;
                  return (
                    <div
                      key={index}
                      className="bg-navy-800/70 backdrop-blur-sm rounded-lg p-4 border border-gold/20 hover:bg-navy-700/70 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="text-2xl font-bold text-gold w-12 drop-shadow-[0_2px_4px_rgba(0,0,0,0.5)]">#{rank}</div>
                          <div className="w-12 h-12 bg-gradient-to-br from-gray-200 to-gray-400 rounded-full flex items-center justify-center border-2 border-gold/30 shadow-md">
                            <span className="text-2xl">👤</span>
                          </div>
                          <div className="text-white font-bold drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">
                            {entry.masked_username || maskUsername(entry.user_name)}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-white">
                            {formatCurrency(entry.wagered)}
                          </div>
                          {prizes[rank] && (
                            <div className="bg-blue-900/95 px-2 py-1 rounded border border-blue-600 shadow-md">
                              <div className="text-yellow-300 font-extrabold text-sm drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                                Ödül: {prizes[rank]}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* No data message */}
        {isEventStarted && leaderboard.length === 0 && (
          <div className="text-center text-white text-xl py-12">
            Veri alınamadı
          </div>
        )}
        
        {/* Event not started message */}
        {!isEventStarted && (
          <div className="text-center text-white text-xl py-12">
            Etkinlik henüz başlamadı. Lütfen bekleyiniz.
          </div>
        )}
      </div>
    </div>
  );
};

export default SpinEventPage;
